import type Phaser from "phaser";
import { VIEW_W } from "@/config";

/**
 * HTML labels that follow world positions (NPC names, the interaction hint).
 * DOM text is crisp at any zoom and supports every character, unlike the 8×8 bitmap font.
 */
export class WorldLabels {
  private root: HTMLDivElement;
  private items = new Map<string, HTMLDivElement>();

  constructor() {
    this.root = document.createElement("div");
    this.root.className = "map-labels";
    document.getElementById("ui")?.appendChild(this.root);
  }

  set(id: string, text: string, className = ""): void {
    let el = this.items.get(id);
    if (!el) {
      el = document.createElement("div");
      this.items.set(id, el);
      this.root.appendChild(el);
    }
    el.className = `map-label ${className}`.trim();
    if (el.textContent !== text) el.textContent = text;
  }

  /** Position a label at a world coordinate (its bottom-centre). */
  place(id: string, scene: Phaser.Scene, worldX: number, worldY: number): void {
    const el = this.items.get(id);
    if (!el) return;
    const cam = scene.cameras.main;
    const rect = scene.game.canvas.getBoundingClientRect();
    const zoom = rect.width / VIEW_W;
    // the container matches the canvas so labels are clipped at its edges
    const rootStyle = this.root.style;
    rootStyle.left = `${Math.round(rect.left)}px`;
    rootStyle.top = `${Math.round(rect.top)}px`;
    rootStyle.width = `${Math.round(rect.width)}px`;
    rootStyle.height = `${Math.round(rect.height)}px`;
    rootStyle.fontSize = `${Math.max(12, Math.round(3.5 * zoom))}px`;
    const sx = (worldX - cam.scrollX) * zoom;
    const sy = (worldY - cam.scrollY) * zoom;
    el.style.transform = `translate(${Math.round(sx)}px, ${Math.round(sy)}px) translate(-50%, -100%)`;
    el.style.display = "";
  }

  hide(id: string): void {
    const el = this.items.get(id);
    if (el) el.style.display = "none";
  }

  destroy(): void {
    this.root.remove();
    this.items.clear();
  }
}
